import type { TranslationStructure } from '../services/translations'

const en: TranslationStructure = {
    audioLesson:{play:'Play',pause:'Pause',seek:'Recording position',missing:'This recording is not available yet.',error:'The recording could not play. Try again.',time:'{{current}} / {{total}} s'},
    labCourse: {"continue":"Continue","tryIt":"Try it","realWorld":"In the real world","stops":"Where the picture stops","why":"Why it matters","lessonCount":"Lesson {{current}} of {{total}}","itemCount":"{{current}} of {{total}}","done":"{{title}}: done.","nextModule":"Next module: {{title}}.","comingSoon":"coming soon","passed":"Gate passed.","notYet":"Not yet.","score":" of {{total}} right the first time","passNote":"You needed {{needed}}. You own this idea now.","failNote":"You need {{needed}}. Go back over the amber ones, then try the gate again.","finish":"Finish the module","retry":"Try the gate again","leaveLine":"Please don't go!","minutesLeft":"Just {{n}} minutes more!","minutesLeftOne":"Just 1 minute more!","lightMode":"Light mode","darkMode":"Dark mode","stayButton":"Okay, I'll stay","leaveAnyway":"Leave anyway","dragStep":"Step {{n}}. Drag up or down, or use the arrow keys.","checkRight":"That's it.","checkWrong":"Not quite. Try another.","inLife":"In life","inTheProgram":"In the program"},
    labProgress: {"close":"Close lesson","saving":"Saving progress...","saveFailed":"Progress could not be saved.","retry":"Try again"},
    nav: {
        home: 'Home',
        learn: 'Learn',
        profile: 'Profile',
    },
    login: {
        common: {
            google: 'Google',
            or: 'or',
        },
        justStartingOut: 'New here?',
        title: 'Welcome back',
        emailPlaceholder: 'Email',
        passwordPlaceholder: 'Password',
        forgotPassword: 'Forgot password?',
        submitButton: 'Log in',
        signUp: 'Create account',
        signingIn: 'Signing you in…',
        forgotPasswordPrompt: 'Enter your email and we\'ll send you a reset link.',
        forgotEmailPlaceholder: 'your@email.com',
        sendLink: 'Send Link',
        cancel: 'Cancel',
        resetSuccess: {
            title: 'Check your email!',
            body: 'We sent a password reset link to',
            backToLogin: 'Back to Login',
            spam: 'Didn\'t receive it? Check your spam folder.',
        },
        errors: {
            invalidEmail: 'Enter a valid email address.',
            passwordRequired: 'Enter your password.',
            emailNotFound: 'This email is not registered',
            oauthOnly: 'Use Google or Facebook to login',
            wrongPassword: 'Check your email and password, then try again.',
            verifyEmail: 'Please verify your email first. Check your inbox!',
            oauthFailed: 'Google sign-in didn’t work. Log in with your email and password below.',
            resetInvalidEmail: 'Enter a valid email address.',
            resetEmailNotFound: 'This email is not registered',
            resetFailed: 'Failed to send reset email. Please try again.',
        },
    },
    signup: {
        common: {
            google: 'Google',
            or: 'or',
        },
        alreadyLearning: 'Already have an account?',
        error: 'Couldn’t create your account. Please try again.',
        title: 'Create account',
        firstNamePlaceholder: 'First name',
        lastNamePlaceholder: 'Last name',
        emailPlaceholder: 'Email',
        passwordPlaceholder: 'Password',
        submitButton: 'Create account',
        logIn: 'Log in',
        // Live strength row under the password: the word by requirements met (0-1, 2, 3, 4),
        // and the screen-reader names of the four chips (8+, Aa, 123, #!)…
        strength: {
            weak: 'Weak',
            fair: 'Fair',
            good: 'Good',
            strong: 'Strong',
            rules: {
                length: '8 or more characters',
                uppercase: 'A capital letter',
                number: 'A number',
                special: 'A symbol',
            },
            met: '{{rule}}, done',
        },
        // …and the single next missing requirement, announced to screen readers only.
        req: {
            length: 'Add 8 or more characters',
            uppercase: 'Add a capital letter',
            number: 'Add a number',
            special: 'Add a symbol',
        },
        errors: {
            invalidEmail: 'Enter a valid email address.',
            emailExists: 'An account with this email already exists',
            weakPassword: 'Use a password that meets the requirements.',
            noAccountFromLogin: "You don't have an account yet. Create one below to get started.",
        },
        heading: 'Create your account',
        stepIndicator: 'Step {{current}} of {{total}}',
        credentialsHeading: 'Email and password',
        continueButton: 'Continue',
        back: 'Back',
    },
    classesPage: {
        yourClasses: "Your classes",
        allClasses: "All classes",
        clearFilters: "Clear filters",
        empty: "New classes are being prepared.",
        categoryEmpty: "No classes here yet.",
        metaTitle: "Classes | DHTN Academy",
        metaDescription: "Explore published classes in technology, design, business, languages and Islamic knowledge at DHTN Academy.",
        categoryFilterLabel: "Filter by subject",
        allSubjects: "All subjects",
        resultsAnnouncement: "Classes shown: {{count}}.",
        progressLabel: "{{percent}}% complete",
        common: {
            dashboard: 'Dashboard',
        },
        title: 'Find your class',
        // The catalog rebuilt on the journey design: one heading, one line, then subject shelves.
        heading: "Classes",
        subheading: 'Every subject we teach. Each one begins with a free course.',
        // Subject sections. `unfiled` covers classes an admin has not categorised yet — the
        // catalog never guesses which subject a class belongs to.
        categories: {
            QURAN: {
                line: "Read the Qur’an with care and confidence",
                label: 'Qur’an',
                blurb: 'Authored by scholars, taught by voice.',
            },
            ISLAMIC_SCIENCES: {
                line: "Know your faith and live it",
                label: 'Islamic knowledge',
                blurb: 'The knowledge that shapes how a life is lived.',
            },
            TECHNOLOGY: {
                line: "Build the things people use every day",
                label: 'Technology',
                blurb: 'Build the things the world now runs on.',
            },
            DESIGN: {
                line: "Make screens that people understand",
                label: 'Design',
                blurb: 'Make things people understand without being told.',
            },
            LANGUAGE: {
                line: "Speak, read and write with confidence",
                label: 'Languages',
                blurb: 'The doors that open every other door.',
            },
            BUSINESS: {
                line: "Start something real and help it grow",
                label: 'Business',
                blurb: 'Lead well, build carefully, be understood.',
            },
            SKILLS_ACQUISITION: {
                line: "Learn a skill and earn from your hands",
                label: 'Skills acquisition',
                blurb: 'A trade in your hands and an honest living.',
            },
            PERSONAL_DEVELOPMENT: {
                line: "Grow into the person you want to be",
                label: 'Personal development',
                blurb: 'Know yourself, lead yourself, then lead others.',
            },
            HISTORY: {
                line: "Learn from the people and nations before us",
                label: 'History',
                blurb: 'How we got here, told properly.',
            },
            unfiled: {
                label: 'More classes',
                blurb: 'Everything else on offer.',
            },
        },
        badgeNew: 'New',
        badgeSoon: 'Coming soon',
        // While searching, the subject shelves collapse into one result shelf.
        // Course-tile caption on the class page.
        freeLabel: 'Free',
        resultsHeading: 'Results',
        resultsCount: '{{count}} classes match.',
        resultsCountOne: '1 class matches.',
        searchPlaceholder: "Search classes",
        allLanguages: 'All languages',
        // Accessible name for the finder's language dropdown. The visible text is the
        // CURRENT language ("All languages", "Arabic"), which does not say what the
        // control does, so the button needs a name of its own.
        languageFilterLabel: 'Filter by language',
        courses: 'courses',
        course: 'course',
        noResults: "No classes match.",
        clearSearch: 'Clear search',
        waitlist: {
            headline: 'Knowledge worth waiting for.',
            sub: 'New classes are being prepared. Join the circle — you\'ll hear from us first.',
            countLabel: 'Join {{count}} others already waiting.',
            placeholder: 'your@email.com',
            button: 'Join the waitlist',
            successHeadline: 'You\'re in the circle.',
            successSub: 'We\'ll reach out the moment new classes open.',
            errorInvalid: 'Please enter a valid email address.',
            errorGeneric: 'Something went wrong. Try again.',
        },
        notifyMe: {
            button: 'Notify Me',
            emailPlaceholder: 'your@email.com',
            submit: 'Notify Me',
            success: 'You\'ll be notified.',
            errorInvalid: 'Please enter a valid email address.',
            errorGeneric: 'Something went wrong. Try again.',
        },
        audienceLabels: {
            YOUNG_LEARNERS: 'Young learners',
            YOUTH: 'Youth',
            EVERYONE: 'Everyone',
            MEN: 'Men',
            WOMEN: 'Women',
        },
    },
    classDetail: {
        courseLabel: "Course",
        goToLesson: "Go to lesson {{n}}",
        lessonCount: "{{n}} lessons",
        lessonCount_one: "{{n}} lesson",
        lessonCount_other: "{{n}} lessons",
        upcomingLesson: "Upcoming lesson",
        reloadClass: "Reload class",
        pageTitle: "Class",
        courseEmpty: "Lessons are being prepared.",
        reviewClass: "Review",
        classComplete: "Class complete.",
        currentLesson: "Next lesson",
        paymentLocked: "Full access required",
        availableLater: "More lessons need full access.",
        savedProgress: "Your progress is saved.",
        progressLabel: "{{percent}}% complete",
        refreshFailed: "Enrollment saved. Reload the class to see your lessons.",
        lessonTypes: {"video": "Video", "audio":"Audio", "quiz": "Quiz", "practice": "Practice", "lesson": "Lesson", "minutes": "min"},
        common: {
            dashboard: 'Dashboard',
        },
        courseComplete: 'Complete',
        overview: {
            duration: 'Duration',
            language: 'Language',
            students: 'Students',
            learningOutcomes: 'What You Will Learn',
            prerequisites: 'Prerequisites',
            curriculum: 'Curriculum',
            tabs: 'Content tabs',
            languageNames: {
                en: 'English',
                ar: 'Arabic',
                ha: 'Hausa',
            },
        },
        enrolled: {
            progress: 'Your Progress',
            stageIndicator: 'Stage {{current}} of {{total}}',
            continueLearning: 'Continue',
            openClass: 'Open class',
            backToDashboard: 'Back to Dashboard',
            expiryWarning: 'Your subscription expires in {{days}} days',
        },
        access: {
            title: 'Class access',
            body: 'Choose full access to unlock every stage and resource.',
            fullAccessTitle: 'Full access',
            fullAccessBody: 'All stages, resources, and the full class path.',
            subscribedBody: 'You have full access.',
            freeOutcome: 'Free to start — no card needed.',
            fullAccessLine: 'Full access · all stages · from {{price}}/mo',
        },
        actions: {
            browseClasses: 'Browse Classes',
            startLearning: 'Start learning',
            startFree: 'Start free',
            enroll: 'Enroll',
            getFullAccess: 'Get full access',
        },
        errors: {
            notFound: 'Class not found',
            enrollFailed: 'Failed to enroll. Please try again.',
        },
    },
    landingPage: {
        heroTagline: 'Knowledge, taught the way it deserves.',
        getStarted: 'Start learning',
        signIn: 'Log in',
        browseClasses: 'Browse classes',
        finalCTALine1: 'Find the class',
        finalCTALine2: 'that fits you.',
        finalCTABody: 'Browse classes. Choose your level. Begin.',
        missionStatement: 'Quran, Islamic sciences, and language classes in clear stages for students and families.',
        institutionName: 'دار الحديث التعليمية',
        learnAbout: 'About DHTN Academy',
        returnToLearning: 'Sign in',
        subjectsStatement: 'The sacred sciences and the modern world, taught with the same rigor and care.',
        subjectsHeadline: 'What you can learn',
        cat: {
            scienceTech: 'Science & Technology',
            recitation: 'Recitation',
            memorization: 'Memorization',
            hausa: 'Hausa',
            more: '+ more',
        },
        pillarsTitle: 'Study areas',
        stand: {
            eyebrow: 'What we stand for',
            title: 'Real knowledge, taught in order.',
            body: 'Quran, the Islamic sciences, language, and modern subjects — taught patiently and in the right order, until what you learn becomes part of how you think.',
        },
        method: {
            bitesized: {
                eyebrow: 'Bite-sized',
                title: 'Bit by bit.',
                body: 'Each lesson is small and finishable — one idea, a few minutes. You move forward every day, and never face a wall.',
            },
            depth: {
                eyebrow: 'Depth',
                title: 'Go deep, move fast.',
                body: 'Real understanding, layer by layer — with steady momentum, so you feel yourself getting better every session.',
            },
            thinking: {
                eyebrow: 'Mastery',
                title: 'Build a sharper mind.',
                body: 'More than memorizing — you learn to think, reason, and solve, in a way that stays with you for life.',
            },
        },
        assurance: {
            eyebrow: 'Built to be trusted',
            title: 'Serious learning, for students and the families who trust us.',
            body: 'Clear stages for students, respectful and easy to follow for families, and structured lessons that help teachers guide every learner.',
        },
        pillar0Title: 'Quran',
        pillar1Title: 'Islamic Sciences',
        pillarLanguagesTitle: 'Languages',
        pillarHadithTitle: 'Hadith Studies',
        pillarFiqhTitle: 'Fiqh',
        pillarTafsirTitle: 'Tafsir',
        pillarAqidahTitle: 'Aqidah',
        pillarSeerahTitle: 'Seerah',
        pillarArabicTitle: 'Arabic Language',
        pillarTajweedTitle: 'Tajweed',
        pillarUsulTitle: 'Usul al-Fiqh',
        pillarNahwTitle: 'Arabic Grammar',
        pillarEnglishTitle: 'English Language',
        pillarMathTitle: 'Mathematics',
        statusActive: 'Active',
        statusComingSoon: 'Coming soon',
        learning: {
            title: 'Clear learning path',
            body: 'Choose a class. Start Stage 1. Keep going.',
            step1: {
                title: 'Choose a class',
                body: 'Find the subject, language, and level that fit.',
            },
            step2: {
                title: 'Start Stage 1',
                body: 'Begin calmly with the first stage.',
            },
            step3: {
                title: 'Keep going',
                body: 'Return to your next lesson anytime.',
            },
        },
        trust: {
            title: 'Built with care',
            body: 'A simple path for students, families, and guided classroom learning.',
            students: {
                title: 'For students',
                body: 'Stages show what to study now and what comes next.',
            },
            family: {
                title: 'For families',
                body: 'Serious, respectful, and easy to understand.',
            },
            returning: {
                title: 'For returning learners',
                body: 'Sign in and continue from your dashboard.',
            },
            teachers: {
                title: 'For guided learning',
                body: 'Clear lessons help teachers and helpers guide every student.',
            },
        },
        classes: 'Classes',
        about: 'About',
        privacy: 'Privacy',
        subjects: {
            mathematics: 'Mathematics',
            engineering: 'Engineering',
            sciences: 'Sciences',
            technology: 'Technology',
            computerScience: 'Computer Science',
            ai: 'Artificial Intelligence',
            arts: 'Arts',
        },
        seo: {
            title: 'DHTN Academy - Structured Islamic learning',
            description: 'Structured Islamic learning in English, Arabic, and Hausa. Browse classes and start Stage 1 free.',
        },
        aria: {
            goToHomepage: 'Go to homepage',
            skipToContent: 'Skip to content',
            subjectsPrev: 'Previous subjects',
            subjectsNext: 'More subjects',
        },
    },
    errorPage: {
        forbidden: { title: "Access unavailable", body: "Your account cannot open this page. Choose another page to continue." },
    "tryAgain": "Try again",
    "inlineMessage": "This section couldn’t load. Please try again.",
    "goBack": "Go back",
    "offline": {
        "title": "You are offline.",
        "body": "Check your connection to continue.",
        "retry": "Try again",
        "backOnline": "You’re back online"
    },
    "serverError": {
        "title": "Something went wrong on our side.",
        "body": "Please try again in a moment.",
        "retry": "Try again",
        "goHome": "Home"
    },
    "notFound": {
        "title": "This page does not exist.",
        "body": "This link may be out of date.",
        "action": "Go to classes",
        "goHome": "Home",
        "goBack": "Go back"
    },
    "sessionExpired": {
        "title": "Please log in again",
        "body": "Your session has ended.",
        "action": "Log in"
    }
},
    aboutPage: {
        eyebrow: 'About',
        heroTitle: 'Learn with purpose.',
        belief1: 'Islamic learning, languages and modern sciences, taught with care.',
        belief2: 'Clear lessons give you time to practise and understand.',
        coda2: 'In Arabic, English and Hausa.',
    },
    footer: {
        tagline: 'Seek knowledge.',
        learnHeading: 'Learn',
        legalHeading: 'Legal',
        navClasses: 'Classes',
        navAbout: 'About',
        navDashboard: 'Dashboard',
    },
    classCard: {
        comingSoon: 'Coming soon',
        hiddenBadge: 'Hidden',
        start: 'Start',
        continue: 'Continue',
        review: 'Review',
        viewClass: 'View class',
    },
    enrollmentSuccess: {
        heading: 'You\'re enrolled.',
        startLearning: 'Start Learning',
        backToClass: 'Back to class',
        stageUnlocked: 'Stage 1 unlocked',
    },
    paymentCallback: {
        missingTitle: "Payment link unavailable",
        missingBody: "Check your account for access. If charged, do not pay again.",
        signinTitle: "Log in to confirm",
        signinBody: "Use the account you paid with.",
        verifyingTitle: "Confirming payment",
        verifyingBody: "We’re checking your payment.",
        successTitle: "Payment confirmed",
        successBody: "Full access is ready.",
        successBodyApp: "Continue in the app or here on the web.",
        returningToApp: 'Taking you back to the DHTN app…',
        openApp: "Open app",
        continueOnWeb: "Continue here",
        successGuideTitle: 'What to do next',
        successWeb: 'Continue learning on the web from this account.',
        successEmail: 'When you use the Android app, sign in with this same email.',
        successAndroidBeta: 'Download the Android app from our website and sign in with this same email.',
        failedTitle: 'Payment not completed',
        failedBody: 'We could not confirm this payment. If your bank shows a debit, contact support with your payment reference.',
        tryAgain: 'Try again',
        continue: "Continue learning",
        confirmingDelayTitle: "Not confirmed yet",
        confirmingDelayBody: "We couldn’t confirm this payment. If charged, do not pay again.",
        retry: 'Check again',
        backToDashboard: "Dashboard",
    },
    unsubscribe: {
        loadingTitle: 'Unsubscribing…',
        doneTitle: 'You’re unsubscribed',
        doneBody: 'You won’t get marketing emails from DHTN Academy anymore. Sign-in, security and password emails will still reach you when needed.',
        invalidTitle: 'This link no longer works',
        invalidBody: 'It may be incomplete or already used. Need help? Email us.',
        backHome: 'Back to DHTN Academy',
    },
    appHandoff: {
        missingTitle: "App link unavailable",
        missingBody: "Continue here, or log in to the app with the same account.",
        desktopBody: "The app is available on Android. You can continue here.",
        title: "Continue learning",
        body: "Open the Android app, or continue here.",
        openApp: "Open app",
        continueOnWeb: "Continue here",
    },
    learningRecords: {
        retry: "Try again",
        loadError: "We couldn’t load your records. Try again.",
        noCertificates: "No certificates yet",
        earnCertificate: "Complete a class to earn your certificate.",
        viewClasses: "View classes",
        viewCertificate: "View certificate",
        history: "Learning history",
        historyBody: "Your learning, day by day.",
        todayDone: "You learned today.",
        todayPending: "Ready when you are.",
        partial: "Some months couldn’t load. Try again for the full record.",
        learned: "Learned",
        today: "Today",
        upcoming: "Upcoming",
        noActivity: "No activity",
        unavailable: "Unavailable",
        hours: "h",
        minutes: "min",
        last12Months: "Last 12 months",
        noHistory: "Your record begins with a lesson.",
        certificate: "Certificate",
        verifyTitle: "Verify certificate",
        verifyError: "Verification unavailable",
        verifyErrorBody: "We couldn’t check this certificate. Try again.",
        shareFailed: "The link couldn’t be shared. Try again or copy it below.",
        linkLabel: "Verification link",
        loading: "Loading records",
        verifyDescription: "Check a certificate issued by DHTN Academy.",
    },

    analyticsPage: {
        back: 'Dashboard',
        title: 'Consistency',
        subtitle: 'A little every day. Here\'s your record.',
        // Open loop (today)
        todayDone: 'You showed up today.',
        todayPending: 'You haven\'t learned today yet — a few minutes keeps it going.',
        // Generous summary
        daysShownUp: "learning days",
        dayShownUp: "learning day",
        longestRun: 'longest run',
        lessonsLifetime: 'lessons',
        timeLifetime: 'learning',
        yourYear: 'Your year',
        yourRecord: 'Your record',
        roomToGrow: 'Your record fills in as you learn. A little every day.',
        // Reminder opt-in (flag-guarded)
        reminderTitle: 'A little every day',
        reminderBody: 'One gentle reminder a day to keep your rhythm. No noise, nothing to lose.',
        reminderCta: 'Turn on a gentle daily reminder',
        // Free-member honest teaser
        freeTeaserTitle: 'See your whole year',
        freeTeaserBody: "Full access includes your last 12 months of learning.",
        freeUpgradeCta: "View plans",
        // Legacy keys (retained; a later /copy pass can prune)
        discoveryTitle: 'Consistency',
        discoverySubtitle: 'Your record, day by day.',
        discoveryCta: 'View record',
        discoveryCtaUpgrade: 'Unlock Premium',
        premiumWallTitle: 'Premium Feature',
        premiumWallBody: 'Unlock your full learning history — yearly activity and more.',
        premiumWallCta: 'Unlock Premium',
    },
    dashboard: {
        welcomeBack: "Welcome back, {{firstName}}.",
        readyToStart: 'Ready to begin?',
        discoverClasses: "Choose a class. Your first course is free.",
        findClasses: "Start learning",
        keepGoingHeading: 'Keep going',
        chooseClass: 'Choose your first class.',
        findClass: 'Find a class',
        nextLessonUnavailable: 'Open your class to choose a lesson.',
        learningHeading: 'Your learning',
        classShelfHeading: 'Your classes',
        learningDays: "days of learning",
        weekSummary: "Practised on {{done}} of {{total}} days this week.",
        dayStreak: 'day streak',
        minutesSoFar: "{{minutes}} minutes this week.",
        continueLearning: 'Continue Learning',
        progress: 'Progress',
        browseClasses: "All classes",
        review: 'Review',
        today: 'today',
        daysActiveThisMonth: 'Days active this month',
        lessonsCompleted: 'lessons completed',
        keepGoing: 'Keep going — you are doing great',
        pickUpWhereYouLeftOff: 'Pick up where you left off',
        stage: 'Stage',
        thisWeek: 'This week',
        continueCourse: 'Continue',
        previousClass: 'Previous class',
        nextClass: 'Next class',
        nextUp: 'Next up',
        viewRoadmap: 'View roadmap',
        momentum: '{{lessons}} lessons · {{time}} · {{days}} days active this month',
        yourProgress: 'Your consistency',
        seeFullHistory: "History",
        todayDone: 'You showed up today. Small and steady is how it\'s built.',
        todayNudge: 'A little today keeps it going.',
        administration: 'Administration',
        adminDashboard: 'Admin dashboard',
        learnerFallback: 'Learner',
        scrollLeft: 'Scroll left',
        scrollRight: 'Scroll right',
        courses: 'courses',
        days: {
            sun: 'Sun',
            mon: 'Mon',
            tue: 'Tue',
            wed: 'Wed',
            thu: 'Thu',
            fri: 'Fri',
            sat: 'Sat',
        },
        yourCertificates: 'Your certificates',
        viewCertificate: 'View',
        analytics: {
            lessons: 'Lessons',
            time: 'Time',
            daysActive: 'Days Active',
            monthlyProgress: 'Monthly Progress',
            yearlyProgress: 'Yearly Progress',
            unlockHistory: 'Unlock full history',
            activityLess: 'Less',
            activityMore: 'More',
            prevYear: 'Previous year',
            nextYear: 'Next year',
            loadError: 'Could not load activity data. Try again.',
        },
        freeBanner: {
            text: 'You\'re on the free plan. Unlock every stage with Full Access.',
            cta: 'Upgrade',
        },
    },
    moduleSelection: {
        accountSettings: 'Account settings',
        signOut: 'Sign out',
        startLearning: 'Start Learning',
        startModule: 'Start',
        continueModule: 'Continue',
        minutes: 'min',
        hours: 'hours',
    },
    auth: {
        signingIn: 'Signing in…',
        pleaseWait: 'Please wait a moment.',
    },
    payment: {
        retry: "Try again",
        error: "Checkout unavailable",
        errorBody: "Check your connection and try again.",
        preparing: "Opening checkout",
        preparingSub: "Complete your payment securely with Paystack.",
        backToPricing: "View plans",
    },
    classOverview: {
        common: {
            dashboard: 'Dashboard',
        },
        locked: 'Locked',
        lockedHint: 'Subscribe to access {{stageName}}',
        minutes: 'min',
        yourJourney: 'Your Learning Journey',
        courses: 'courses',
        nextChapterAwaits: 'Your next chapter awaits',
        unlockNextStage: 'Unlock {{nextStageName}} and keep learning',
        continueJourney: 'Get full access',
        stageLabel: 'Stage',
        complete: 'complete',
        learners: 'learners',
        stages: 'stages',
        classComplete: 'Class complete!',
        classCompleteBody: 'You\'ve finished every lesson in this class.',
        upToDate: 'You\'re all caught up',
        upToDateBody: 'You\'ve finished every lesson available so far. New lessons are on the way.',
        comingSoon: 'Coming soon',
        reviewNeeded: 'Needs review',
        continueHint: 'Continue with full access',
        reviewLessonsCount: '{{count}} lessons to review',
        reviewNow: 'Review now',
    },
    classroom: {
        checkAnswer: 'Check answer',
        nextQuestion: 'Next question',
        submitAnswer: 'Submit answer',
        continue: 'Continue',
        completeQuiz: 'Complete quiz',
        loading: 'Loading lesson...',
        exitLesson: 'Exit lesson',
        lessonNotFound: 'Lesson not found',
        returnCourse: 'Back to course',
        connectionError: 'Connection problem',
        connectionErrorDesc: 'Could not load the lesson. Please check your internet connection and try again.',
        questionOfTotal: 'Question {{current}} of {{total}}',
        selectAll: 'Select all that apply',
        videoNotAvailable: 'Video not available',
        noQuestions: 'No questions',
        practiceNotConfigured: 'Practice not set up',
        typeAnswer: 'Your answer',
        correct: 'Correct!',
        incorrect: 'Not quite',
        tryAgain: 'Try Again',
        review: 'Review',
        reviewComplete: 'All caught up',
        reviewCompleteSubtitle: 'Every review is done. Keep going.',
        reviewCompleteContext: 'You finished reviewing {{course}} · Stage {{stage}}',
        reviewContinue: 'Continue',
        lessonNotConfigured: 'Lesson not configured',
        lessonResources: 'Lesson Resources',
        downloadResource: 'Download',
        lessonSlides: 'Lesson Slides',
        premiumOnly: 'Premium plan required to download',
        premiumResourceLocked: 'Premium only',
        tapToRetry: 'Tap to retry',
        masterySaveError: 'We could not save this mastery check. Please try again.',
        hearIt: 'Hear it',
        muteSounds: 'Mute sounds',
        unmuteSounds: 'Unmute sounds',
        recite: {
            compare: 'Compare',
            record: 'Record',
            recordAgain: 'Record again',
            stop: 'Stop',
            yours: 'Your recitation',
            listenReference: 'Hear the reference',
            recitedAloud: 'I recited it aloud',
            micDenied: 'Microphone unavailable — recite aloud, then continue.',
            recordFirst: 'Record yourself first — then compare.',
        },
        explain: {
            reveal: 'See a clear answer',
            model: 'A clear answer',
            placeholder: 'Write it in your own words…',
        },
        selfCheck: {
            title: 'How did that go?',
            practicing: 'Keep practicing',
            gotIt: "I've got it",
        },
        trace: {
            instruction: 'Trace over the letter with your finger',
            clear: 'Clear',
            done: 'Well traced — tap Continue',
        },
        step: {
            next: 'Reveal the next step',
            done: 'That is the reasoning — tap Continue',
        },
        letters: {
            tapToHear: 'Tap a letter to hear it',
        },
        instruction: {
            fillGap: 'Fill in the gap',
            explain: 'Explain it in your own words',
        },
        code: {
            run: 'Run',
            format: 'Format',
            running: 'Running…',
            preparing: 'Preparing Python…',
            preparingNote: 'Setting up the Python runtime — a one-time download (about 6 MB). Runs instantly after this.',
            allPassed: 'All tests passed',
            someFailed: 'Some tests failed',
            test: 'Test',
            expected: 'expected',
            got: 'got',
            pythonError: 'Could not load the Python runtime. Check your connection.',
            output: 'Output',
            reset: 'Reset',
            copy: 'Copy',
            copied: 'Copied',
        },
        level: {
            beginner: 'Beginner',
            intermediate: 'Intermediate',
            advanced: 'Advanced',
        },
        exitModal: {
            title: 'Stay a little longer?',
            stay: 'Keep going',
            leave: 'Leave anyway',
        },
        // ── New atoms (2026-07-16) — generic UI labels; per-atom instructions are authored ──
        why: 'Why?',
        whyModal: {
            title: 'Why this is the answer',
            gotIt: 'Got it',
        },
        speak: {
            reveal: 'Done',
            hint: 'Speak your answer aloud, then rate yourself — your voice is never uploaded.',
        },
        glossary: {
            hint: 'Tap an underlined word to see what it means.',
        },
        recap: {
            title: 'What you learned',
        },
        flashcard: {
            front: 'Front',
            back: 'Back',
            tapToFlip: 'Tap to flip',
            lockHint: 'Flip it a few times — the quiet repetition fixes it in memory.',
            gotIt: 'Got it',
        },
        estimate: {
            reveal: 'Reveal',
            hint: 'Commit to a guess first — it makes the answer stick.',
        },
        terminal: {
            placeholder: 'Type a command…',
            hint: 'Press Enter to run it — this is a safe practice shell.',
        },
        checkpoint: {
            submit: 'Submit for review',
            submitted: 'Submitted',
        },
        parsons: {
            empty: 'Tap or drag blocks here…',
            remove: 'Remove',
            hint: 'Tap a block to place it, or drag to reorder — nothing locks.',
            program: 'Program',
            blocks: 'Blocks',
            allPlaced: '— all placed —',
        },
        connect: {
            step: 'Step {{n}}',
            hint: 'Tap the parts in the order a request flows.',
        },
        hotspot: {
            hint: 'Hover to explore each part, then tap the one the question asks for.',
            wrong: 'Not that one — look again',
        },
        branch: {
            tryAnother: 'Try a different path',
            good: 'You handled it well',
            bad: 'That went wrong',
        },
        dialogue: {
            wrong: 'Not quite — try another reply',
        },
    },
    settings: {
        saveError: "Couldn’t save your profile. Try again.",
        deleteError: "Couldn’t delete your account. Try again.",
        sessionsLoadError: "Couldn’t load your devices. Try again.",
        sessionsActionError: "Some devices could not be signed out. Try again.",
        unknownDevice: "Unknown device",
        computer: "Computer",
        retry: "Try again",
        firstName: "First name",
        lastName: "Last name",
        accountSettings: "Settings",
        subtitle: 'Manage your profile and preferences',
        backToDashboard: 'Dashboard',
        profile: 'Profile',
        preferences: 'Preferences',
        preferencesDesc: "Appearance and playback.",
        sections: {
            notifications: 'Notifications',
            notificationsDesc: 'Choose how DHTN Academy reaches you.',
            membership: 'Membership',
            security: 'Security',
            securityDesc: 'Sign-in and the devices on your account.',
        },
        membership: {
            fullAccess: 'Full Access',
            freePlan: 'Free plan',
            everyStageUnlocked: 'Every stage, every class unlocked',
            renewsOn: 'Renews {{date}}',
            freeBody: "Your first course is free.",
            upgradeCta: "View plans",
            coveredUntil: "Access until {{date}}.",
            autoChargeNote: 'We never charge your card automatically — renew whenever you\'re ready.',
            renewCta: "Renew",
        },
        dataManagement: "Your data",
        interfaceLanguage: "Language",
        courseNotifications: 'Course notifications',
        notificationsDesc: "Progress updates and reminders.",
        autoPlayVideos: 'Auto-play videos',
        autoPlayDesc: "Play the next video automatically.",
        sounds: 'Sounds',
        soundsDesc: 'Gentle cues as you practice.',
        exportData: "Download data",
        exporting: 'Exporting…',
        exportError: 'Export failed. Please try again.',
        exportDesc: "Get a copy of your account details and learning progress.",
        deleteDesc: 'Permanently delete your account and all learning progress. This action cannot be undone.',
        saveChanges: 'Save changes',
        cancel: 'Cancel',
        signOut: 'Sign out',
        activeSessions: "Your devices",
        sessionsTab: 'Sessions',
        sessionsDescription: "Devices signed in to your account.",
        currentDevice: 'Current device',
        signOutDevice: 'Sign out',
        signOutAllOtherDevices: "Sign out others",
        noActiveSessions: "No devices to show.",
        lastActive: 'Last active',
        saving: 'Saving…',
        saved: 'Saved',
        whatsapp: {
            heading: "WhatsApp",
            phone: "WhatsApp number",
            phoneHint: "Include your country code, such as +234.",
            optIn: 'Receive announcements on WhatsApp',
            save: 'Save',
            helper: "Choose whether to receive announcements.",
            saveError: 'Could not save. Try again.',
        },
        emailFieldLabel: 'Email',
        emailCannotChange: 'Email cannot be changed here. Contact support if needed.',
        addPasswordTitle: "Sign in with email",
        addPasswordDesc: "Add a password to use your email at login.",
        addPasswordCta: 'Add password',
        newPassword: "New password",
        confirmPassword: "Confirm password",
        passwordPlaceholder: 'At least 8 characters',
        confirmPlaceholder: 'Re-enter password',
        savePassword: "Save password",
        passwordSetSuccess: "Password saved. You can now log in with email.",
        passwordSetFail: "Couldn’t save your password. Try again.",
        networkError: "Check your connection and try again.",
        passwordTooShort: 'Password must be at least 8 characters',
        passwordMismatch: 'Passwords do not match',
        theme: 'Theme',
        themeDark: 'Dark',
        themeLight: 'Light',
        themeSystem: 'System',
        themeDescSystem: 'Following your system theme preference.',
        themeDescDark: 'Always use dark mode.',
        themeDescLight: 'Always use light mode.',
        languageAutoSave: "Saved automatically.",
        exportTitle: "Download your data",
        exportComingSoon: 'Data export feature coming soon!',
        deleteTitle: "Delete account",
        deleting: 'Deleting...',
        deleteConfirmTitle: 'Delete account?',
        deleteConfirmMsg: 'This will permanently delete your account and all learning progress. This action cannot be undone.',
        deleteFinalTitle: "Delete permanently?",
        deleteFinalMsg: 'You are about to permanently delete your account. All enrolled courses and progress will be lost. This action cannot be undone.',
        deleteBtnLabel: "Delete my account",
        firstNamePlaceholder: 'First name',
        lastNamePlaceholder: 'Last name',
        continue: 'Continue',
    },
    expiry: {
        title: 'Your access has ended',
        body: 'Subscribe to continue learning and keep all your progress.',
        subscribeCta: 'SUBSCRIBE',
        dismissCta: 'Not now',
    },
    expiryBanner: {
        message: 'Your full access ends in {{days}} days.',
        messageToday: 'Your full access ends today.',
        renewCta: 'Renew',
    },
    subscriptionModal: {
        title: 'Full Access',
        subtitle: 'Complete access to every stage and material.',
        cta: 'Proceed to Payment',
    },
    paywall: {
        paymentValidationError: "Payment could not start. Choose a plan and try again.",
        yearlySaving: '25% less than paying monthly.',
        signupFirst: 'Create an account, then pay through Paystack.',
        paymentError: 'Checkout could not open. Check your connection and try again.',
        eyebrow: 'Full access',
        headlineLocked: 'Unlock {{stageName}}',
        headlineExpired: 'Return to learning',
        headlineDefault: 'Full access',
        headlineRenew: 'Keep learning',
        leadRenew: 'Add more time to your full access.',
        lead: 'Every stage of every class.',
        leadExpired: 'Your progress is saved. Renew to continue.',
        continue: 'Continue',
        maybeLater: 'Maybe later',
        back: 'Back',
        close: 'Close',
        perMonthBilledYearly: '{{price}} per month, paid yearly.',
        honestBilling: 'One payment covers {{period}}. We never charge your card automatically — renew only when you choose.',
        promise: 'One payment. No automatic renewal.',
        periodMonth: 'one month',
        periodYear: 'one year',
        noCommitment: 'No commitment',
        noAutoCharge: 'No auto-charge, ever',
        secure: 'Secure payment by Paystack',
        goBack: 'Go back',
        offerTitle: 'Not ready for a year?',
        offerBody: 'Start monthly instead — {{price}} for a month of full access. No commitment, and we never auto-charge.',
        offerStartMonthly: 'Start monthly',
        offerDecline: 'No thanks, go back',
    },
    // The in-flow "doors" shown as a lesson step (peak-win after the free course, cliffhanger after
    // a teaser lesson). Kept to one line + price + one button.
    door: {
        winEyebrow: 'You did it',
        winHeadline: 'You finished your first course.',
        winBody: 'Keep this going — one membership opens every stage of every class.',
        winPrimary: 'Keep learning',
        moreEyebrow: 'Keep going',
        moreHeadline: 'This is where {{courseTitle}} really begins.',
        moreBody: 'You have had the first lesson. Open the whole course — and every other one.',
        morePrimary: 'Continue this course',
        priceLine: '{{price}} a year · no auto-charge, ever',
        notNow: 'Not now',
        saveEyebrow: 'Nice start',
        saveHeadline: 'Save your progress',
        saveBody: 'You are already learning. Create a free profile so your progress is saved and you can keep going.',
        savePrimary: 'Create free profile',
        namePlaceholder: 'Your name',
        fillAll: 'Please add your name, email, and a password.',
        error: 'Something went wrong. Please try again.',
    },
    onboarding: {
        title1: 'What do you want to learn?',
        sub1: 'Pick one to begin. You can explore the rest anytime.',
        title2: 'Where are you starting from?',
        sub2: "We'll start you in a good place — you can move around anytime.",
        levelNew: "I'm new to this",
        levelSome: 'I know some already',
        levelAdvanced: "I'm quite advanced",
        back: 'Back',
        lessonLoading: 'Getting your first lesson ready…',
    },
    verifyNudge: {
        body: "Confirm your email.",
        resend: 'Resend link',
        sent: 'Link sent. Check your inbox.',
        dismiss: 'Dismiss',
    },
    completionCard: {
        premiumTeaser: 'Premium members get a shareable completion card',
        unlockCta: 'UNLOCK COMPLETION CARD',
    },
    lessonComplete: {
        eyebrow: 'Lesson complete',
        continue: 'Continue',
    },
    moduleComplete: {
        label: 'Module done',
        continue: 'Continue',
        headline: 'Module {{n}} done.',
        headlineLast: 'Last module done.',
        position: '{{k}} of {{n}}',
        nextModule: 'Next module',
        finishCourse: 'Finish the course',
    },
    courseComplete: {
        label: 'Course done',
        title: 'Course complete',
        coursesInStage: 'courses in this stage',
        nextCourse: 'Next course',
        backToClass: "Back to class",
        subscribeCta: 'Go further',
        startReview: 'Start Review',
        questionsToReview: 'questions to review',
        upgradeHint: 'Unlock all stages with Full Access',
        upgradeCta: 'Upgrade',
        checkBackSoon: "View next course",
        continue: 'Continue',
        subtitle: 'Well done. Another course behind you.',
        reviewMistake: "Review 1 question",
        reviewMistakes: "Review {{count}} questions",
        youKnow: 'You know',
        statModule: '1 module',
        statModules: '{{count}} modules',
        statLesson: '1 lesson',
        statLessons: '{{count}} lessons',
        statMastery: '{{percent}} mastery',
        mastery: {
            title: 'Your result',
            masteryScore: "Mastery",
            accuracy: 'Accuracy',
            lessonsMastered: 'Lessons mastered',
            of: 'of',
            loadError: "We couldn’t load your score. Try again.",
            noAttempts: 'Complete the graded lessons to see your score.',
            improved: "Up from {{prior}}%",
            sameBest: 'You matched your best score of {{prior}}%',
            stillBest: 'Your best score is still {{prior}}%. Keep practicing.',
            before: 'Before',
            now: 'Now',
        },
    },
    stageComplete: {
        eyebrow: 'Stage complete',
        nextStage: 'Next stage',
        subtitle: 'A whole stage behind you.',
        beginStage: 'Begin Stage {{stage}}',
        upgradeTitle: 'You just finished Stage {{stage}}',
        upgradeBody: 'Unlock every stage, course and lesson with full access.',
        upgradeCta: "View plans",
        upgradeInvite: "Full Access includes the remaining stages.",
    },
    lessonComingSoon: {
        titleLesson: "Coming soon",
        titleCourse: "Coming soon",
        bodyLesson: "The next lesson is being prepared.",
        bodyCourse: "The next course is being prepared.",
        backToClass: 'Back to class',
    },
    pricingPage: {
        eyebrow: 'DHTN access',
        title: 'Choose access',
        subtitle: 'Choose Full Access when you are ready for every stage.',
        primaryCardTitle: 'One account',
        primaryLead: 'Your access works on web and mobile.',
        billingLabel: 'Billing cycle',
        includedTitle: 'Included with Full Access',
        freeTitle: 'Start free',
        freeBody: 'Stage 1 is open in every class. No card required.',
        freePointStage: 'Stage 1 of every class',
        freePointPreview: 'Class overview and curriculum preview',
        freePointNoCard: 'No card required to begin',
        afterTitle: 'After payment',
        afterPay: 'Pay through Paystack using card or bank options.',
        afterClass: 'Your account is updated after payment is confirmed.',
        afterMobile: 'Open the mobile app and sign in with the same account.',
        secureTitle: 'Secure payment',
        footerNote: 'Secure payment by Paystack. DHTN never stores your card details.',
        socialProof: 'Your progress stays on your DHTN account.',
        featuresLabel: 'Plan features',
        aria: {
            close: 'Close pricing page',
        },
        goToDashboard: 'Dashboard',
        toggle: {
            monthly: 'Monthly',
            yearly: 'Yearly',
            saveBadge: 'Save 25%',
        },
        period: {
            month: '/month',
            year: '/year',
        },
        free: {
            name: 'Free',
            period: 'forever',
            subtitle: 'First stage of every class, at no cost.',
            cta: 'Browse classes',
        },
        fullAccess: {
            name: 'Full Access',
            cta: 'Continue to pay',
            badge: 'Complete Access',
            yearlySaving: 'Best value — billed annually.',
            monthlyNote: 'Monthly access.',
        },
        feature: {
            stage1: 'First stage of every class',
            videosQuizzes: 'Video lessons and quizzes',
            allStages: 'All stages unlocked',
            studyMat: 'Downloadable resources',
            certificate: 'Completion certificate',
            everythingInFree: 'Everything in Free',
            webMobile: 'Access on web and mobile',
        },
    },
    verifyEmail: {
        title: 'Check your email',
        subtitle: 'Confirm your email address.',
        sentTo: 'We sent a confirmation email to',
        sentGeneric: "Look for the confirmation email in your inbox.",
        instructions: 'Open the link in your email to confirm your address.',
        verifying: 'Verifying email…',
        verifyingBody: 'Please wait while we verify your email address.',
        successTitle: 'Email verified',
        successBody: "You can now log in.",
        successCta: 'Log in',
        errorTitle: 'Verification failed',
        resendPrompt: "Enter your email for a new link.",
        didntReceive: "No email yet?",
        emailPlaceholder: 'your@email.com',
        resendButton: 'Resend email',
        resentTitle: "Email sent",
        resentConfirm: "Check your inbox for the new link.",
        backToLogin: 'Back to log in',
        errors: {
            emailRequired: 'Please enter your email address',
            failed: 'Verification failed. The link may have expired.',
            resendFailed: 'Failed to resend. Please try again.',
        },
    },
    forgotPassword: {
        title: 'Forgot password?',
        subtitle: 'Enter your email and we\'ll send a reset link.',
        emailPlaceholder: 'your@email.com',
        submitButton: 'Send reset link',
        sentTitle: 'Check your email',
        sentBody: 'We sent a password reset link to',
        sentHint: "If you have an account with this email, open the reset link in your inbox.",
        errors: {
            invalidEmail: 'Enter a valid email address.',
            sendFailed: 'Failed to send reset email. Try again.',
        },
    },
    resetPassword: {
        title: 'Set a new password',
        subtitle: 'Choose a new password for your account.',
        newPasswordPlaceholder: 'New password',
        confirmPasswordPlaceholder: 'Confirm password',
        submitButton: 'Update password',
        successTitle: 'Password updated',
        successBody: 'Log in with your new password.',
        successCta: 'Log in',
        expiredTitle: 'This link has expired',
        expiredBody: 'Reset links work once, for a short time. Send yourself a new one.',
        newLinkCta: 'Send a new link',
        req: {
            length: '8+ characters',
            uppercase: '1 uppercase letter',
            number: '1 number',
            special: '1 special character (!@#$%^&*)',
        },
        errors: {
            noToken: 'Invalid reset link. Please request a new one.',
            mismatch: 'Passwords do not match',
            weak: 'Password does not meet all requirements',
            failed: 'Failed to reset password. Try again.',
        },
    },
    achievements: {
        title: 'Certificates',
        subtitle: 'Your verified certificates of completion.',
        verified: 'Verified',
        noCertificates: 'No certificates yet. Complete a full class to earn your certificate.',
        premiumRequired: 'Certificates are available for Premium subscribers.',
        downloading: 'Downloading...',
    },
    // The certificate DOCUMENT is fixed-English (a document of record — see
    // CertificateDocument.tsx); these keys cover only the page chrome.
    certificate: {
        title: 'Certificate of completion',
        certNumberLabel: 'Certificate No.',
        download: "Download",
        downloading: "Preparing download",
        downloadFailed: 'Download failed. Please try again.',
        notFound: 'Certificate not found.',
        preparing: "Loading certificate",
        share: 'Share',
        shareCopied: 'Link copied',
        verifyValid: "Certificate verified",
        verifyInvalid: "Certificate not found",
        verifyInvalidMessage: "No certificate matches this number. Check the link and try again.",
        // Growth surface: a stranger who just verified a real credential is a warm
        // lead — invite them in rather than dead-end them "home". Routes to /.
        verifyReturnHome: 'Visit DHTN Academy',
        verifyIssuedBy: 'Issued by DHTN Academy',
        verifiableAt: 'Verify this certificate at',
        holderLabel: 'Awarded to',
        classLabel: 'Class',
        completedLabel: 'Completed',
    },
    classComplete: {
        title: 'Class complete',
        viewCertificate: "View certificate",
        viewCertificates: "View certificates",
        browseClasses: "View classes",
        premiumOnly: 'Premium subscribers receive a certificate for completing a class.',
        upgradeForCertificate: 'Upgrade for Certificate',
        unlockCertificate: 'Unlock Certificate',
        subtitlePremium: "Your certificate is ready.",
        subtitlePreparing: "Your certificate isn’t available yet.",
        subtitleFree: "You've completed the whole class — a real milestone.",
        claimLine: "Full Access includes certificates.",
        claimCta: "View plans",
        welcomeHome: "Welcome home.",
        seeCertificate: "See your certificate",
        yoursToKeep: "Yours to keep.",
        nextJourney: "Choose your next journey",
        openCertificate: "Open the full certificate",
        completedTheClass: "has completed the class",
        skip: "Skip",
        courseCount: "{{count}} courses",
        courseCount_one: "{{count}} course",
        courseCount_other: "{{count}} courses",
        lessonCount: "{{count}} lessons",
        lessonCount_one: "{{count}} lesson",
        lessonCount_other: "{{count}} lessons",
    },
    welcome: {
        intro: {
            sentence: "Let's find your perfect class.",
        },
        skip: 'Skip',
        step1: {
            title: 'Who is learning?',
            youngLearner: 'Young Learner',
            youth: 'Youth',
            men: 'Men',
            women: 'Women',
            everyone: 'Anyone',
        },
        step2: {
            title: 'What are you interested in?',
            subtitle: 'Select all that apply',
            islamic_sciences: 'Islamic Sciences',
            quran: 'Quran',
            languages: 'Languages',
            stem: 'STEM',
            all: 'View all',
            continue: 'Continue',
            createAccount: 'Create Account',
            browseClasses: 'Browse Classes',
        },
        step3: {
            title: 'Choose your plan',
            monthly: 'Monthly',
            yearly: 'Yearly',
            savePercent: 'Save {{n}}%',
            mostPopular: 'Most Popular',
            browseFree: 'BROWSE FREE',
            getBasic: 'GET BASIC',
            getPremium: 'GET PREMIUM',
        },
    },
    // Public page for deleting an account without the app (Google Play requirement, 9 Oct 2026).
    deleteAccountPage: {
        title: "Delete your account",
        headline: "Delete account",
        intro: "You can delete your DHTN Academy account and everything in it.",
        signedInTitle: "Can you sign in?",
        signedInBody: "Open Settings, then Delete account. It happens at once and cannot be undone.",
        openSettings: "Open Settings",
        emailTitle: "Cannot sign in?",
        emailBefore: "Email",
        emailAfter: "from your account's email address. We delete the account within 30 days.",
        deletedTitle: "What is deleted",
        deletedAccount: "Your account and profile",
        deletedProgress: "Your progress and the classes you joined",
        deletedCertificates: "Your certificates",
        deletedPayments: "Your payment records, unless the law requires us to keep them",
        nothingKept: "Nothing else is kept after deletion.",
    },
    app: {
        offlineWarning: 'No internet connection',
        announcementDefaultCta: 'Learn more',
    },
}

export default en
